"use client";

import { useTranslations } from "@i18n/react";
import { useRef, useState } from "react";

import { uploadToPresignedUrl } from "../utils/image-uploads/uploadToPresignedUrl";

export type SelectedImage = {
  url: string;
  s3Path: string;
  originalSrc?: string;
  lastCropState?: { crop: { x: number; y: number }; zoom: number };
};

export type PresignedResult = {
  url: string;
  fields: Record<string, string>;
  s3_path: string;
};

export const useImageUpload = ({
  getPresignedUrl,
  onUploadComplete,
}: {
  getPresignedUrl: () => Promise<PresignedResult>;
  onUploadComplete: (image: SelectedImage) => void;
}) => {
  const t = useTranslations();
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const previewUrlRef = useRef<string | null>(null);

  const upload = async (
    blob: Blob,
    meta?: Pick<SelectedImage, "originalSrc" | "lastCropState">,
  ): Promise<SelectedImage | null> => {
    setIsUploading(true);
    setError(null);
    try {
      const presigned = await getPresignedUrl();
      await uploadToPresignedUrl(presigned, blob);
      const objectUrl = URL.createObjectURL(blob);
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = objectUrl;
      const image: SelectedImage = {
        url: objectUrl,
        s3Path: presigned.s3_path,
        ...meta,
      };
      onUploadComplete(image);
      return image;
    } catch {
      setError(t("events_image_upload_error"));
      return null;
    } finally {
      setIsUploading(false);
    }
  };

  return {
    upload,
    isUploading,
    error,
    clearError: () => setError(null),
  };
};
